import UAParser from 'ua-parser-js'

interface AnalyticsConfig {
  endpoint: string
  user?: string
  sessionId?: string
}
export interface AnalyticsEvent {
  eventType: string
  eventData: string | number | Object
  user?: string
  metadata: {
    [key: string]: any
  }
}

class Analytics {
  private config: AnalyticsConfig
  private eventQueue: AnalyticsEvent[] = []
  private userAgent: any = null
  private ipInfo: any = null
  private defaultMetadata: Record<string, any> = {}
  // private lastEventTimestamp: number = Date.now()
  private flushTimeout: NodeJS.Timeout | null = null
  private idleFlushTime = 5 * 60 * 1000
  private lastCallTime: Date | null = null
  private lastCallTimeoutLimit = 3 * 60 * 1000
  // private priorityTwoStackSize = 5
  private priorityThreeStackSize = 20

  public events = {
    PAGE_VIEW: 'page_view',
    ORDER_SUCCESS: 'order_success',
    ORDER_FAILED: 'order_failed',
    CONTENT_VIEW: 'content_view',
    CONTENT_PLAY: 'content_play',
    CONTENT_WATCH_TIME: 'content_watch_time',
    CONTENT_PAUSE: 'content_pause',
    CONTENT_COMPLETED: 'content_completed',
    CONTENT_NEXT: 'content_next',
    CONTENT_PREV: 'content_prev',
  }

  private constructor(config: AnalyticsConfig) {
    this.config = config
  }

  /**
   * Static method to create and initialize an Analytics instance
   * @param config
   * @returns {Promise<Analytics>}
   */
  static async initialize(config: AnalyticsConfig): Promise<Analytics> {
    const instance = new Analytics(config)
    await instance.init()
    return instance
  }

  /**
   * Analytics initiator
   * @private
   */
  private async init() {
    const parser = new UAParser()
    this.userAgent = parser.getResult()
    this.ipInfo = await this.getIPInfo()
    this.lastCallTime = new Date()

    this.defaultMetadata = {
      url: window.location.href,
      referrer: document.referrer,
      userIp: this.ipInfo?.userIp,
      location: this.ipInfo?.location,
      os: this.userAgent?.os?.name,
      device: this.userAgent?.device?.type || 'other',
      browser: this.userAgent?.browser?.name,
      screenSize: {
        width: window.screen.width,
        height: window.screen.height,
      },
      // @ts-ignore
      networkType: navigator?.connection?.effectiveType || 'unknown',
    }

    const storedEvents = localStorage.getItem('analyticsEvents')
    if (storedEvents) {
      this.eventQueue = JSON.parse(storedEvents)
      this.flushEvents()
    }
    window?.addEventListener('beforeunload', () => this.flushEvents())
  }

  /**
   * Capture analytic event
   * @param eventType
   * @param eventData
   * @param priority
   * @param additionalMetadata
   */
  async captureEvent(
    eventType?: AnalyticsEvent['eventType'],
    eventData?: AnalyticsEvent['eventData'],
    priority: 1 | 2 | 3 = 3,
    additionalMetadata?: Record<string, any>
  ) {
    const event =
      eventData && eventType
        ? {
            ...(eventType && { eventType }),
            ...(eventData && { eventData }),
            user: this.config.user,
            metadata: {
              ...this.defaultMetadata,
              ...additionalMetadata,
            },
          }
        : null

    if (eventData && eventType && event) {
      this.eventQueue.push(event as AnalyticsEvent)
      localStorage.setItem('analyticsEvents', JSON.stringify(this.eventQueue))
      this.resetFlushTimeout()
    }
    switch (priority) {
      case 1:
        this.flushEvents()
        break
      case 2:
        const currentTime = new Date()
        const minutesDifference =
          (+currentTime - +(this.lastCallTime || new Date())) / (1000 * 60)

        if (
          minutesDifference >=
          this.lastCallTimeoutLimit / (1000 * 60) //|| // Convert ms to minutes for comparison
          // this.eventQueue.length >= this.priorityTwoStackSize
        ) {
          this.lastCallTime = currentTime
          this.flushEvents()
        }

        break

      case 3:
        if (this.eventQueue.length >= this.priorityThreeStackSize) {
          this.flushEvents()
        }
        break
      default:
        break
    }
  }

  private resetFlushTimeout() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
    }

    this.flushTimeout = setTimeout(() => {
      this.flushEvents()
      console.log('resetFlushTimeout called')
    }, this.idleFlushTime)
  }

  async flushEvents() {
    if (this.eventQueue.length === 0) return
    const eventsToSend = [...this.eventQueue]
    this.eventQueue = []
    try {
      await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventsToSend),
      })
      localStorage.removeItem('analyticsEvents')
    } catch (error) {
      console.error('Analytics', 'Failed to send events', error)
      this.eventQueue.push(...eventsToSend)
      localStorage.setItem('analyticsEvents', JSON.stringify(this.eventQueue))
    }
    //  finally {
    //   this.lastEventTimestamp = Date.now()
    // }
  }

  private async getIPInfo() {
    try {
      const response = await fetch('https://freeipapi.com/api/json').then(
        (res) => res.json()
      )
      if (response) {
        return {
          userIp: response.ipAddress,
          location: {
            country: response.countryName,
            region: response.regionName,
            city: response.cityName,
            coordinates: {
              latitude: response.latitude,
              longitude: response.longitude,
            },
          },
        }
      }
    } catch (err) {
      return {}
    }
  }
}
export default Analytics
